import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Image, Palette, Check, X, Loader2, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${BASE_URL}${path}`;
};

const getHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * ImagePicker - Reusable image selection with 3 sources:
 *   - Upload (from device)
 *   - My Inspo (user's saved inspiration images)
 *   - Host Gallery (host-curated images, only shown when eventId provided)
 *
 * Props:
 *   selectedImages: string[] - array of image URLs currently selected
 *   onSelect: (images: string[]) => void - callback with updated selections
 *   eventId?: string - if provided, shows Host Gallery tab
 *   category?: string - filter inspo by category
 *   maxImages?: number - max selectable images (default 10)
 *   compact?: boolean - smaller layout for inline use
 */
export const ImagePicker = ({
  selectedImages = [],
  onSelect,
  eventId,
  category,
  maxImages = 10,
  compact = false
}) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [userInspo, setUserInspo] = useState([]);
  const [hostInspo, setHostInspo] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingInspo, setLoadingInspo] = useState(false);
  const fileRef = useRef(null);

  // Load user inspo
  useEffect(() => {
    setLoadingInspo(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    fetch(`${API}/inspirations?${params}`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const items = Array.isArray(data) ? data : (data.inspirations || data.items || []);
        setUserInspo(items);
      })
      .catch(() => {})
      .finally(() => setLoadingInspo(false));
  }, [category]);

  // Load host inspo if eventId provided
  useEffect(() => {
    if (!eventId) return;
    fetch(`${API}/rsvp/${eventId}/host-inspo`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setHostInspo(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [eventId]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    const newUrls = [];

    for (const file of files.slice(0, maxImages - selectedImages.length)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API}/upload/image`, {
          method: 'POST',
          headers: getHeaders(),
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          newUrls.push(data.url);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newUrls.length > 0) {
      onSelect([...selectedImages, ...newUrls]);
      toast.success(`${newUrls.length} image${newUrls.length > 1 ? 's' : ''} uploaded`);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleImage = (url) => {
    if (selectedImages.includes(url)) {
      onSelect(selectedImages.filter(u => u !== url));
    } else if (selectedImages.length < maxImages) {
      onSelect([...selectedImages, url]);
    } else {
      toast.error(`Max ${maxImages} images`);
    }
  };

  const tabs = [
    { id: 'upload', label: 'Upload', icon: Upload },
    { id: 'inspo', label: 'My Inspo', icon: Image, count: userInspo.length },
    ...(eventId ? [{ id: 'host', label: 'Host Gallery', icon: Palette, count: hostInspo.length }] : [])
  ];

  const renderGrid = (items, keyPrefix) => {
    if (!items.length) {
      return <p className="text-xs text-gray-400 text-center py-4">No images available</p>;
    }
    return (
      <div className={`grid ${compact ? 'grid-cols-4 gap-1.5' : 'grid-cols-3 gap-2'}`}>
        {items.map((item, i) => {
          const url = getImageUrl(item.image_url || item.url || item);
          if (!url) return null;
          const isSelected = selectedImages.some(s => getImageUrl(s) === url || s === (item.image_url || item.url || item));
          return (
            <button
              key={`${keyPrefix}-${item.id || item.inspo_id || item.inspiration_id || i}`}
              onClick={() => toggleImage(item.image_url || item.url || item)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-transparent hover:border-gray-300'
              }`}
              data-testid={`image-pick-${keyPrefix}-${i}`}
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
              {isSelected && (
                <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                  <Check className="w-5 h-5 text-white drop-shadow" />
                </div>
              )}
              {item.is_private && (
                <Badge className="absolute top-1 left-1 text-[8px] bg-black/50 text-white border-0">Private</Badge>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="image-picker">
      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`image-picker-tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {tab.count > 0 && <span className="text-[10px] text-gray-400">({tab.count})</span>}
          </button>
        ))}
      </div>

      {/* Selected count */}
      {selectedImages.length > 0 && (
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-purple-600 font-medium">{selectedImages.length} selected</span>
          <button onClick={() => onSelect([])} className="text-xs text-gray-400 hover:text-red-500">Clear all</button>
        </div>
      )}

      {/* Tab content */}
      <div className={`${compact ? 'max-h-48' : 'max-h-64'} overflow-y-auto`}>
        {activeTab === 'upload' && (
          <div className="space-y-2">
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-gray-200 rounded-xl py-6 flex flex-col items-center gap-2 hover:border-purple-300 hover:bg-purple-50/30 transition-all"
              data-testid="image-upload-btn"
            >
              {uploading ? (
                <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-gray-400" />
              )}
              <span className="text-xs text-gray-500">{uploading ? 'Uploading...' : 'Tap to upload from device'}</span>
            </button>
            {/* Show selected uploads as preview */}
            {selectedImages.length > 0 && (
              <div className={`grid ${compact ? 'grid-cols-4 gap-1.5' : 'grid-cols-3 gap-2'}`}>
                {selectedImages.map((url, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                    <img src={getImageUrl(url)} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => onSelect(selectedImages.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/60 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      title="Remove image (revert to default)"
                      data-testid={`image-revert-${i}`}
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'inspo' && (
          loadingInspo ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : renderGrid(userInspo, 'inspo')
        )}

        {activeTab === 'host' && renderGrid(hostInspo, 'host')}
      </div>
    </div>
  );
};

export default ImagePicker;
